<script setup>
console.log('🚀 GM Response Time Chart: Initializing...');

import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chart, registerables } from 'chart.js';
import GmChartControls from '../filters/GmChartControls.vue';
import GmAnalyticsAPI from 'dashboard/api/gmAnalytics';

Chart.register(...registerables);

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Response Time Trend'
  },
  height: {
    type: Number,
    default: 300
  },
  channels: {
    type: Array,
    default: () => []
  },
  agents: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits(['refresh', 'export', 'filters-change']);

const { t } = useI18n();

console.log('📊 GM Response Time Chart: Props received:', {
  data: props.data,
  title: props.title
});

// Reactive data
const isLoading = ref(false);
const lastUpdated = ref(null);
const filters = ref({
  dateRange: { from: null, to: null, preset: 'last_30_days' },
  period: { type: 'month', value: null },
  channel: null,
  agent: null
});
const filteredData = ref([]);
const chartInstance = ref(null);
const chartCanvas = ref(null);

// Chart configuration
const chartConfig = ref({
  type: 'bar',
  data: {
    labels: [],
    datasets: [{
      label: 'Response Time (minutes)',
      data: [],
      backgroundColor: 'rgba(59, 130, 246, 0.8)',
      borderColor: '#3b82f6',
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#3b82f6',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const minutes = context.parsed.y;
            return formatTime(minutes * 60);
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f3f4f6',
          drawBorder: false
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12
          },
          callback: function(value) {
            return formatTime(value * 60);
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  }
});

// Helper function to format time
const formatTime = (seconds) => {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`;
  } else {
    return `${Math.floor(seconds / 3600)}h`;
  }
};

// Helper function to get response time color based on performance
const getResponseTimeColor = (minutes) => {
  if (minutes <= 5) return '#10b981'; // Green - Excellent
  if (minutes <= 15) return '#f59e0b'; // Yellow - Good
  if (minutes <= 30) return '#f97316'; // Orange - Fair
  return '#ef4444'; // Red - Poor
};

// Process data for chart
const processChartData = (rawData) => {
  if (!rawData || rawData.length === 0) {
    return {
      labels: [],
      data: []
    };
  }
  
  // Process real data
  const labels = rawData.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  
  const data = rawData.map(item => (item.avgResponseTime || 0) / 60); // Convert seconds to minutes
  
  return { labels, data };
};

// Computed properties
const chartData = computed(() => {
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  const processed = processChartData(dataToUse);
  return {
    ...chartConfig.value,
    data: {
      ...chartConfig.value.data,
      labels: processed.labels,
      datasets: [{
        ...chartConfig.value.data.datasets[0],
        data: processed.data
      }]
    }
  };
});

// Chart statistics
const chartStats = computed(() => {
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  if (!dataToUse || dataToUse.length === 0) return null;
  
  const values = dataToUse.map(item => (item.avgResponseTime || 0) / 60);
  const total = values.reduce((sum, val) => sum + val, 0);
  const average = total / values.length;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const current = values[values.length - 1] || 0;
  const previous = values[values.length - 2] || 0;
  const trend = current - previous;
  
  return {
    average: Math.round(average * 10) / 10,
    max: Math.round(max * 10) / 10,
    min: Math.round(min * 10) / 10,
    current: Math.round(current * 10) / 10,
    trend: Math.round(trend * 10) / 10,
    currentColor: getResponseTimeColor(current)
  };
});

// Methods
const updateChart = async () => {
  console.log('🔄 GM Response Time Chart: Updating chart with new data...');
  
  if (!chartCanvas.value) return;
  
  // Destroy existing chart if it exists
  if (chartInstance.value) {
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
  
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  
  if (!dataToUse || dataToUse.length === 0) {
    console.log('📊 GM Response Time Chart: No data to display');
    return;
  }
  
  const labels = dataToUse.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  
  const data = dataToUse.map(item => (item.avgResponseTime || 0) / 60); // Convert to minutes
  
  const ctx = chartCanvas.value.getContext('2d');
  
  chartInstance.value = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: labels,
      datasets: [{
        label: 'Response Time (minutes)',
        data: data,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: '#3b82f6',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#3b82f6',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              const value = context.parsed.y;
              if (value === 0) return 'Response Time: 0s';
              if (value < 1) return `Response Time: ${(value * 60).toFixed(0)}s`;
              return `Response Time: ${value.toFixed(1)}m`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12
            }
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#f3f4f6',
            drawBorder: false
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12
            },
            callback: function(value) {
              if (value === 0) return '0s';
              if (value < 1) return `${(value * 60).toFixed(0)}s`;
              return `${value.toFixed(0)}m`;
            }
          }
        }
      }
    }
  });
  
  console.log('✅ GM Response Time Chart: Chart updated successfully');
};

const fetchChartData = async () => {
  console.log('🔄 GM Response Time Chart: Fetching chart data with filters:', filters.value);
  try {
    isLoading.value = true;
    
    // Make API call to get filtered response time trend data
    const response = await GmAnalyticsAPI.getResponseTimeTrend(filters.value);
    console.log('🔍 GM Response Time Chart: API response:', response);
    
    if (response.data && response.data.success) {
      // Update the chart data with filtered results
      filteredData.value = response.data.data.responseTimeTrend || [];
      console.log('✅ GM Response Time Chart: Filtered data received:', filteredData.value);
    }
    
    lastUpdated.value = new Date().toISOString();
    console.log('✅ GM Response Time Chart: Chart data fetched successfully');
    
    // Update the chart with new data
    await updateChart();
  } catch (error) {
    console.error('❌ GM Response Time Chart: Error fetching chart data:', error);
    // Fallback to original data if API fails
    filteredData.value = props.data || [];
    await updateChart();
  } finally {
    isLoading.value = false;
  }
};

const refreshChart = () => {
  console.log('🔄 GM Response Time Chart: Refreshing chart...');
  isLoading.value = true;
  
  // Emit refresh event to parent
  emit('refresh', { chartType: 'response-time', filters: filters.value });
  
  // Simulate loading
  setTimeout(() => {
    isLoading.value = false;
    lastUpdated.value = new Date().toISOString();
    updateChart();
  }, 1000);
};

const exportChart = () => {
  console.log('📊 GM Response Time Chart: Exporting chart...');
  emit('export', { chartType: 'response-time', filters: filters.value });
};

const onFiltersChange = (newFilters) => {
  console.log('🔧 GM Response Time Chart: Filters changed:', newFilters);
  filters.value = newFilters;
  emit('filters-change', { chartType: 'response-time', filters: newFilters });
  fetchChartData();
};

// Watch for external filter changes
watch(() => props.filters, (newFilters) => {
  if (newFilters) {
    console.log('🔧 GM Response Time Chart: External filters changed:', newFilters);
    filters.value = { ...filters.value, ...newFilters };
    updateChart();
  }
}, { deep: true });

// Lifecycle
onMounted(async () => {
  console.log('🎯 GM Response Time Chart: Component mounted');
  lastUpdated.value = new Date().toISOString();
  await nextTick();
  await updateChart();
});

console.log('✅ GM Response Time Chart: Component setup completed');
</script>

<template>
  <div class="gm-response-time-chart">
    <!-- Chart Controls -->
    <GmChartControls
      :title="title"
      icon="⏱️"
      :is-loading="isLoading"
      :last-updated="lastUpdated"
      :show-additional-filters="true"
      :channels="channels"
      :agents="agents"
      v-model="filters"
      @refresh="refreshChart"
      @export="exportChart"
      @filters-change="onFiltersChange"
    />
    
    <!-- Chart Stats -->
    <div v-if="chartStats" class="gm-chart-stats">
      <div class="gm-stat-item">
        <span class="gm-stat-label">Current</span>
        <span class="gm-stat-value" :style="{ color: chartStats.currentColor }">
          {{ formatTime(chartStats.current * 60) }}
        </span>
      </div>
      <div class="gm-stat-item">
        <span class="gm-stat-label">Avg</span>
        <span class="gm-stat-value">{{ formatTime(chartStats.average * 60) }}</span>
      </div>
      <div class="gm-stat-item">
        <span class="gm-stat-label">Peak</span>
        <span class="gm-stat-value">{{ formatTime(chartStats.max * 60) }}</span>
      </div>
    </div>
    
    <div class="gm-chart-container">
      <div v-if="filteredData.length === 0 && (!props.data || props.data.length === 0)" class="gm-chart-placeholder">
        <div class="gm-chart-message">
          <div class="gm-chart-icon-large">📊</div>
          <h3>No Response Time Data Available</h3>
          <p>Response time data will appear here once available</p>
        </div>
      </div>
      <div v-else class="gm-chart-canvas-container">
        <canvas ref="chartCanvas"></canvas>
      </div>
    </div>
    
    <div v-if="chartStats" class="gm-chart-footer">
      <div class="gm-trend-indicator">
        <span class="gm-trend-icon" :class="{ 'positive': chartStats.trend < 0, 'negative': chartStats.trend > 0 }">
          {{ chartStats.trend < 0 ? '↗️' : chartStats.trend > 0 ? '↘️' : '➡️' }}
        </span>
        <span class="gm-trend-text">
          {{ chartStats.trend < 0 ? 'Faster' : chartStats.trend > 0 ? 'Slower' : 'Same' }} by {{ Math.abs(chartStats.trend).toFixed(1) }}m
          <span class="gm-trend-period">vs previous period</span>
        </span>
      </div>
      <div class="gm-performance-indicator">
        <span class="gm-performance-label">Performance:</span>
        <span 
          class="gm-performance-value" 
          :style="{ color: chartStats.currentColor }"
        >
          {{ chartStats.current <= 5 ? 'Excellent' : chartStats.current <= 15 ? 'Good' : chartStats.current <= 30 ? 'Fair' : 'Poor' }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-response-time-chart {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  max-height: 500px;
  overflow: hidden;
}

.gm-response-time-chart:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.gm-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.gm-chart-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.gm-chart-icon {
  font-size: 1.125rem;
}

.gm-chart-stats {
  display: flex;
  gap: 1rem;
}

.gm-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.gm-stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.gm-stat-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.gm-chart-container {
  margin-bottom: 1rem;
  height: 300px;
  overflow: hidden;
}

.gm-chart-canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.gm-chart-placeholder {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.gm-chart-message {
  text-align: center;
  padding: 2rem;
  max-width: 500px;
}

.gm-chart-icon-large {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.gm-chart-message h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.gm-chart-message p {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.gm-chart-sample-data {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  gap: 0.5rem;
  height: 120px;
  padding: 0 1rem;
}

.gm-sample-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  max-width: 60px;
}

.gm-bar-container {
  height: 80px;
  width: 20px;
  background: #e5e7eb;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}

.gm-bar-fill {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 2px;
  transition: height 0.3s ease;
  min-height: 4px;
}

.gm-bar-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  text-align: center;
}

.gm-bar-value {
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
}

.gm-chart-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f3f4f6;
  padding-top: 1rem;
}

.gm-trend-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gm-trend-icon {
  font-size: 1rem;
}

.gm-trend-icon.positive {
  color: #10b981;
}

.gm-trend-icon.negative {
  color: #ef4444;
}

.gm-trend-text {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.gm-trend-period {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 400;
  margin-left: 0.25rem;
}

.gm-performance-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gm-performance-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.gm-performance-value {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
}

@media (max-width: 768px) {
  .gm-response-time-chart {
    padding: 1rem;
  }
  
  .gm-chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .gm-chart-stats {
    width: 100%;
    justify-content: space-around;
  }
  
  .gm-chart-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .gm-chart-sample-data {
    gap: 0.25rem;
    padding: 0 0.5rem;
  }
  
  .gm-sample-bar {
    max-width: 40px;
  }
  
  .gm-bar-container {
    width: 16px;
    height: 60px;
  }
}
</style>
